import { redirect } from "react-router-dom";
import useURL from "shared/welda/hooks/useURL";
import { reduxStore } from "../stores/reduxStore";

// Helper function to wait for loading to complete
/**
 * Waits for the Redux user loading state to complete before proceeding.
 * This prevents premature route protection checks while user data is being fetched.
 * @param {number} maxWaitTime - Maximum time to wait in milliseconds (default: 5000ms)
 * @returns {Promise<void>} Resolves when loading is complete or max time is reached
 */
const waitForLoadingComplete = (maxWaitTime = 5000) => {
  return new Promise((resolve) => {
    const startTime = Date.now();

    const checkState = () => {
      try {
        const state = reduxStore.getState();
        const isLoading = state.users.isLoading;
        const hasAttemptedAuth = state.users.hasAttemptedAuth;
        const user = state.users.user;
        const client = state.users.client;

        // Login already completed in Redux — don't wait on background auth checks.
        if (user || client) {
          resolve();
          return;
        }

        // Proceed once auth check finished
        if (hasAttemptedAuth && !isLoading) {
          resolve();
          return;
        }

        // Check if we've exceeded max wait time
        if (Date.now() - startTime > maxWaitTime) {
          console.warn(
            "Route protection: Max wait time exceeded, proceeding with current state"
          );
          resolve();
          return;
        }

        // Wait a bit and check again
        setTimeout(checkState, 100);
      } catch (error) {
        console.error(
          "Route protection: Error while waiting for loading state:",
          error
        );
        // If there's an error, resolve anyway to prevent infinite waiting
        resolve();
      }
    };

    checkState();
  });
};

const requireClient = (client) => {
  const { URLPrefix } = useURL();
  if (!client) {
    throw redirect(`${URLPrefix}auth/login`);
  }
  return client;
};

const requireEmployee = (user, request) => {
  if (!user) {
    const url = new URL(request.url);
    const returnTo = `${url.pathname}${url.search}`;
    // Preserve query (e.g. /?openCustomer=12345) so sticker QR deep-links survive login
    if (returnTo === "/" || returnTo === "") {
      throw redirect(`/login`);
    }
    throw redirect(`/login?redirect=${encodeURIComponent(returnTo)}`);
  }
  return user;
};

const withUserState =
  (fn) =>
    async ({ request }) => {
      // Only wait for loading if we're actually checking user authentication
      // If fn is null, we're just providing context, so don't wait
      if (fn) {
        // Wait for loading to complete before checking user state
        await waitForLoadingComplete();
      }

      const state = reduxStore.getState();
      const user = state.users.user;

      if (fn) {
        return fn(user, request);
      }
      return { user };
    };

const withClientState = (fn) => async () => {
  // Wait for loading to complete before checking client state
  await waitForLoadingComplete();
  const state = reduxStore.getState();
  const client = state.users.client;

  if (fn) {
    return fn(client);
  }
  return { client };
};

export { requireClient, requireEmployee, withClientState, withUserState };
